import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import { SubdomainSection } from "@/common/components/SubdomainSection";
import { TefIdentifierSection } from "@/common/components/TefIdentifierSection";
import { EstablishmentTimezoneSection } from "@/features/establishments/components/EstablishmentTimezone";
import AccreditationSection from "@/features/establishments/components/GeneralData/AccreditationSection";
import { BankAccountSection } from "@/features/establishments/components/GeneralData/BankAccountSection";
import BlockUnblockEstablishment from "@/features/establishments/components/GeneralData/BlockUnblockEstablishment";
import ContractSection from "@/features/establishments/components/GeneralData/ContractSection";
import CreatorSection from "@/features/establishments/components/GeneralData/CreatorSection";
import DisableEnableEstablishment from "@/features/establishments/components/GeneralData/DisableEnableEstablishment";
import DisplayAndSalesRestrictionAccordion from "@/features/establishments/components/GeneralData/DisplayAndSalesRestrictions";
import GeneralDataSection from "@/features/establishments/components/GeneralData/GeneralDataSection";
import { useEstablishmentAllowedClientCompanies } from "@/features/establishments/hooks/useEstablishmentAllowedClientCompanies";
import useEstablishmentCompany from "@/features/establishments/hooks/useEstablishmentCompany";
import { EstablishmentAllowedClientCompanyData } from "@/features/establishments/types/establishmentAllowedClientCompanies.type";
import { useParams } from "react-router-dom";

const GeneralData = () => {
  const { id } = useParams();

  if (!id) {
    return null;
  }

  const {
    data: establishmentCompany,
    isPending: establishmentCompanyIsPending,
    isLoading: establishmentCompanyIsLoading,
    error: establishmentCompanyError = {} as any,
    refetch: refetchEstablishmentCompany,
    isRefetching: establishmentCompanyIsRefetching
  } = useEstablishmentCompany(id);

  const {
    data: establishmentAllowedClientCompanies,
    isPending: establishmentAllowedClientCompaniesIsPending,
    isFetched: establishmentAllowedClientCompaniesIsFetched,
    isRefetching: establishmentAllowedClientCompaniesIsRefetching
  } = useEstablishmentAllowedClientCompanies<EstablishmentAllowedClientCompanyData[]>({ id });

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-192px)]"
      statusCode={establishmentCompanyError?.status}
      refetchFunction={refetchEstablishmentCompany}
    >
      <div className="flex flex-col bg-white-100 rounded-md center p-4 gap-4 overflow-y-auto max-h-[calc(100vh-192px)] w-full">
        <BlockUnblockEstablishment
          establishment={establishmentCompany}
          disableButtons={establishmentCompanyIsPending}
        />

        <DisableEnableEstablishment
          establishment={establishmentCompany}
          disableButtons={establishmentCompanyIsPending}
        />

        <SubdomainSection
          url={establishmentCompany?.domains?.[0].host}
          establishmentCompany={establishmentCompany}
          establishmentCompanyIsPending={establishmentCompanyIsPending}
        />

        <EstablishmentTimezoneSection
          establishment={establishmentCompany}
          isEstablishmentPending={establishmentCompanyIsPending}
        />

        <TefIdentifierSection
          identifier={establishmentCompany?.matrix_branch_authorizer_identifier}
          matrixBranchLatestTefDatasetCreatedAt={establishmentCompany?.matrix_branch_latest_tef_dataset_created_at}
          matrixBranchLatestTefDatasetVersion={establishmentCompany?.matrix_branch_latest_tef_dataset_version}
          matrixBranchId={establishmentCompany?.matrix_branch_id}
          establishmentCompanyId={id}
          establishmentCompanyIsRefetching={establishmentCompanyIsRefetching}
          establishmentCompanyIsPending={establishmentCompanyIsPending}
        />

        <CreatorSection
          establishmentCompany={establishmentCompany}
          isEstablishmentPending={establishmentCompanyIsPending}
        />

        <ContractSection
          establishmentCompany={establishmentCompany}
        />

        <DisplayAndSalesRestrictionAccordion
          establishmentCompany={establishmentCompany}
          establishmentAllowedClientCompanies={establishmentAllowedClientCompanies}
          establishmentAllowedClientCompaniesIsRefetching={establishmentAllowedClientCompaniesIsRefetching}
          establishmentAllowedClientCompaniesIsFetched={establishmentAllowedClientCompaniesIsFetched}
          disabled={establishmentAllowedClientCompaniesIsPending}
        />

        <AccreditationSection
          establishmentCompany={establishmentCompany}
          establishmentCompanyIsLoading={establishmentCompanyIsLoading}
        />

        <GeneralDataSection
          establishmentCompany={establishmentCompany}
          establishmentCompanyIsPending={establishmentCompanyIsPending}
        />

        <BankAccountSection
          establishmentCompanyId={establishmentCompany?.id}
          establishmentCompanyBranchId={establishmentCompany?.matrix_branch_id}
          bankAccountInformation={establishmentCompany?.matrix_branch?.bank_account_information}
          establishmentCompanyIsLoading={establishmentCompanyIsLoading}
        />
      </div>
    </ErrorMessageWrapper>
  );
};

export default GeneralData;
